import {
	Box,
	Button,
	FormControl,
	FormLabel,
	Input,
	Textarea,
	VStack,
	Text,
	useToast,
	Heading,
	FormErrorMessage,
} from '@chakra-ui/react';
import { useState, useContext } from 'react';
import { sendContactForm } from 'src/services/contact';
import UserContext from 'src/contexts/user/UserContext';

interface ContactFormProps {
	initialMessage?: string;
	onSuccess?: () => void;
}

const ContactForm = ({ initialMessage = '', onSuccess }: ContactFormProps) => {
	const { user } = useContext(UserContext);
	const toast = useToast();
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [submitted, setSubmitted] = useState(false);
	const [formData, setFormData] = useState({
		name: user?.name || '',
		email: user?.email || '',
		phone: user?.phone || '',
		subject: '',
		message: initialMessage,
	});
	const [errors, setErrors] = useState<Record<string, string>>({});

	const validateEmail = (email: string): boolean => {
		// RFC 5322 compliant email regex
		const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/;
		return emailRegex.test(email);
	};

	const validate = () => {
		const newErrors: Record<string, string> = {};
		if (!formData.name.trim()) {
			newErrors.name = 'Name is required';
		}
		if (!formData.email.trim()) {
			newErrors.email = 'Email is required';
		} else if (!validateEmail(formData.email)) {
			newErrors.email = 'Please enter a valid email address (e.g., name@domain.com)';
		}
		if (!formData.subject.trim()) {
			newErrors.subject = 'Subject is required';
		}
		if (!formData.message.trim()) {
			newErrors.message = 'Message is required';
		}
		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const handleChange = (
		e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
	) => {
		const { name, value } = e.target;
		setFormData((prev) => ({ ...prev, [name]: value }));
		if (errors[name]) {
			setErrors((prev) => ({ ...prev, [name]: '' }));
		}
	};

	const parseBackendError = (error: unknown): string => {
		const err = error as { response?: { data?: { message?: string } } };
		const message = err?.response?.data?.message;
		if (!message) return 'Please try again later.';

		// Parse validation errors like '"email" must be a valid email'
		const fieldErrors: string[] = [];
		if (message.includes('"email"')) {
			fieldErrors.push('Please enter a valid email address');
		}
		if (message.includes('"name"')) {
			fieldErrors.push('Name is required');
		}
		if (message.includes('"subject"')) {
			fieldErrors.push('Subject is required');
		}
		if (message.includes('"message"')) {
			fieldErrors.push('Message is required');
		}

		return fieldErrors.length > 0 ? fieldErrors.join('. ') : message;
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!validate()) return;

		setIsSubmitting(true);
		try {
			await sendContactForm(formData);
			setSubmitted(true);
			toast({
				title: 'Message sent',
				description: "We'll get back to you as soon as possible.",
				status: 'success',
				duration: 5000,
				isClosable: true,
			});
			onSuccess?.();
		} catch (error) {
			const errorMessage = parseBackendError(error);
			toast({
				title: 'Failed to send message',
				description: errorMessage,
				status: 'error',
				duration: 7000,
				isClosable: true,
			});
		} finally {
			setIsSubmitting(false);
		}
	};

	if (submitted) {
		return (
			<Box p={6} textAlign="center">
				<Heading size="md" mb={4} color="green.500">
					Thank you!
				</Heading>
				<Text color="gray.600">
					Your message has been sent. We'll get back to you as soon as
					possible.
				</Text>
				<Button
					mt={6}
					variant="outline"
					onClick={() => {
						setSubmitted(false);
						setFormData({
							name: user?.name || '',
							email: user?.email || '',
							phone: user?.phone || '',
							subject: '',
							message: '',
						});
					}}
				>
					Send another message
				</Button>
			</Box>
		);
	}

	return (
		<Box p={6} h="full" overflowY="auto">
			<Heading size="md" mb={2}>
				Contact Support
			</Heading>
			<Text color="gray.600" mb={6} fontSize="sm">
				Have a question or need help? Send us a message and we'll get back to
				you.
			</Text>
			<form onSubmit={handleSubmit}>
				<VStack spacing={4} align="stretch">
					<FormControl isInvalid={!!errors.name} isRequired>
						<FormLabel fontSize="sm">Name</FormLabel>
						<Input
							name="name"
							value={formData.name}
							onChange={handleChange}
							placeholder="Your name"
							size="sm"
						/>
						<FormErrorMessage>{errors.name}</FormErrorMessage>
					</FormControl>

					<FormControl isInvalid={!!errors.email} isRequired>
						<FormLabel fontSize="sm">Email</FormLabel>
						<Input
							name="email"
							type="email"
							value={formData.email}
							onChange={handleChange}
							placeholder="your@email.com"
							size="sm"
						/>
						<FormErrorMessage>{errors.email}</FormErrorMessage>
					</FormControl>

					<FormControl isInvalid={!!errors.phone}>
						<FormLabel fontSize="sm">Phone (optional)</FormLabel>
						<Input
							name="phone"
							type="tel"
							value={formData.phone}
							onChange={handleChange}
							placeholder="Your phone number"
							size="sm"
						/>
						<FormErrorMessage>{errors.phone}</FormErrorMessage>
					</FormControl>

					<FormControl isInvalid={!!errors.subject} isRequired>
						<FormLabel fontSize="sm">Subject</FormLabel>
						<Input
							name="subject"
							value={formData.subject}
							onChange={handleChange}
							placeholder="What is this about?"
							size="sm"
						/>
						<FormErrorMessage>{errors.subject}</FormErrorMessage>
					</FormControl>

					<FormControl isInvalid={!!errors.message} isRequired>
						<FormLabel fontSize="sm">Message</FormLabel>
						<Textarea
							name="message"
							value={formData.message}
							onChange={handleChange}
							placeholder="How can we help you?"
							rows={6}
							size="sm"
						/>
						<FormErrorMessage>{errors.message}</FormErrorMessage>
					</FormControl>

					<Button
						type="submit"
						colorScheme="orange"
						isLoading={isSubmitting}
						loadingText="Sending..."
						w="full"
					>
						Send Message
					</Button>
				</VStack>
			</form>
		</Box>
	);
};

export default ContactForm;
